import React from 'react';
import { useStyles, useTheme, VariantColor } from '@archipro-design/aria';
import * as S from './ColorPicker.mobile.style';
import type { BasePickerProps } from './OptionPicker';
import type AttributeOption from '../../type/attribute-option';
import ProductAttributeEnum from '../../config/product-attribute';
import OptionHeader from './OptionHeader.mobile';
import { useVariantOptionState } from '../../context/VariantOptionContext';
import { useVariantDecision } from '../../hook/use-variant-decision';
import ColorCarousel from './ColorCarousel.mobile';
import { AngleDown } from '@archipro-design/icons';

type ColorPickerProps = BasePickerProps & {
    data: {
        options: AttributeOption[];
    };
};

const emptyOption: AttributeOption = {
    ID: 0,
    Code: 'transparent',
    Label: 'Not Applicable',
    Attribute: {
        ID: ProductAttributeEnum.Colour,
        Name: 'Colour',
    },
};

const ColorPicker = ({
    data: { variants, options },
    isGeneric,
}: ColorPickerProps) => {
    const { decision } = useVariantOptionState();
    const { pickOption } = useVariantDecision(variants);
    const [expanded, setExpanded] = React.useState(false);
    const { css } = useStyles({ expanded });
    const theme = useTheme();

    const selectedOptionID = decision.options[ProductAttributeEnum.Colour] ?? 0;

    const option =
        options.find((opt) => opt.ID === selectedOptionID) ?? emptyOption;

    const items = [...options];

    if (!isGeneric) {
        items.unshift(emptyOption);
    }

    const toggleExpanded = () => {
        setExpanded((e) => !e);
    };

    const imageUrl = option.Image ? option.Image.Mobile : null;

    const image = imageUrl
        ? {
              width: 26,
              height: 26,
              src: imageUrl,
              alt: option.Label || '',
          }
        : null;

    return (
        <div className={css(S.ColorPicker)}>
            <OptionHeader
                title="Colour"
                // render indicator outside so it can be relative to the outer container
                hasManyOptions={false}
                subTitle={
                    <div className={css(S.SelectedColor)}>
                        <VariantColor
                            size={26}
                            colorCode={image ? undefined : option.Code ?? ''}
                            image={image}
                            variables={{
                                containerBorder: '0 none',
                                border: '0 none',
                                ringColor:
                                    theme.siteVariables.colors.charcoal[250],
                            }}
                        />
                        {option.Label}
                    </div>
                }
                expanded={expanded}
                onClick={toggleExpanded}
            />
            <div className={css(S.Indicator)} onClick={toggleExpanded}>
                <AngleDown />
            </div>
            <ColorCarousel
                onSelect={(option) =>
                    pickOption({
                        ID: option.ID,
                        attributeID: ProductAttributeEnum.Colour,
                    })
                }
                selectedID={option.ID}
                colors={items}
                visible={expanded}
            />
        </div>
    );
};

export default ColorPicker;
